import { computeWeights } from './query';
import isNaN from 'lodash/isNaN';
import toNumber from 'lodash/toNumber';

/**
 * 将结果集空值处理
 * @param {*} data
 * @param {*} keys 对应需要处理的key集合
 * @param {*} symbol 替换符号
 * @returns
 */
export const handleDataOrNull = (data, keys = [], symbol = '--') => {
  const result = [];
  if (!keys || keys.length == 0) {
    return data;
  }
  data?.forEach(item => {
    let obj = { ...item };
    keys.forEach(key => {
      const value = item[key];
      if (!value) {
        obj[key] = symbol;
      }
    });
    result.push(obj);
  });
  return result;
};

/**
 * 将数字格式化为指定格式字符串
 *
 * @param {*} num - 需要格式化的数字。
 * @returns 格式化后的数字字符串，或者如果传入的参数不是数字，则返回空字符串。
 */
export function formatNumber(num) {
  // 判断是否为数字
  if (isNaN(num)) {
    return '';
  }

  // 将数字转换为字符串，以便操作
  let strNum = num.toString();

  // 如果是整数，直接返回
  if (Number.isInteger(num)) {
    return strNum;
  }

  // 如果是小数，处理小数点后的零
  let decimalIndex = strNum.indexOf('.');
  if (decimalIndex !== -1) {
    let decimalPart = strNum.substring(decimalIndex + 1);
    // 移除小数点后的所有零，包括末尾的零
    decimalPart = decimalPart.replace(/0+$/, '');
    // 如果小数部分全由零组成，则返回整数部分
    if (decimalPart === '') {
      return strNum.substring(0, decimalIndex);
    }
    // 否则，更新小数部分
    strNum = strNum.substring(0, decimalIndex + decimalPart.length + 1);
  }

  return strNum;
}

/**
 * 格式化对象属性值，保留指定小数位数
 *
 * @param numberValue 要格式化的数字值
 * @param demLen 保留的小数位数
 * @returns 格式化后的字符串或原值（如果numberValue不是数字）
 */
export function formatToTwoDecimalPlaces(value, demLen = 2) {
  // 确保输入的值为数字类型，使用 lodash 的 _.toNumber 方法转换
  const numberValue = toNumber(value);

  // 如果转换后的值为 NaN，返回原始输入
  if (isNaN(numberValue)) {
    return value;
  }

  // 四舍五入到两位小数
  // const roundedValue = Math.round(numberValue * 100) / 100;
  return computeWeights(Number(numberValue), demLen)
  // 将结果转换为字符串并保留两位小数
  // return roundedValue.toFixed(2);
}

/**
 * 格式化对象属性值，保留指定小数位数
 *
 * @param obj 待格式化的对象
 * @param demLen 保留的小数位数，默认为2
 * @returns 格式化后的对象
 */
export function formatObjectValues(obj, demLen = 2) {
  const formattedObj = {};

  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      formattedObj[key] = formatToTwoDecimalPlaces(obj[key], demLen);
    }
  }

  return formattedObj;
}



/**
 * 对数据进行处理，可以是数组或对象
 * @param {Array<Object>|Object} data - 包含多个对象的数组或单个对象
 * @returns {Array<Object>|Object} 处理后的数据，所有数值都被四舍五入并保留两位小数
 */
export function roundValues(data) {
  if (Array.isArray(data)) {
    // 如果是数组，逐一处理数组中的每个对象
    return data.map(item => formatObjectValues(item));
  } else if (typeof data === 'object' && data !== null) {
    // 如果是对象，直接处理这个对象
    return formatObjectValues(data);
  }

  // 如果输入既不是对象也不是数组，返回原始输入
  return data;
}

/**
 * 处理数字函数
 *
 * @param value 待处理的数字或字符串
 * @returns 返回处理后的数字，如果无法转换为数字，则返回原值
 */
export function processNumber(value) {
  // 尝试将值转换为数字
  const number = parseFloat(value);

  // 检查是否为有效数字
  if (isNaN(number)) {
    return value; // 不能转为数字，返回原值
  }

  // 如果数值为 0.0000，则返回 0
  if (number === 0) {
    return 0;
  }

  // 保留两位小数
  return parseFloat(number.toFixed(2));
}

/**
 * 处理对象中的值，如果值为数组则处理数组中的每个元素，否则直接处理该值
 *
 * @param input 需要处理的对象 或者数组
 * @returns 处理后的对象
 */
export function processObject(input) {
  if (Array.isArray(input)) {
    // 如果传入的是数组，格式化数组的每一项
    return input.map(processObject);
  } else if (typeof input === 'object' && input !== null) {
    // 如果传入的是对象，格式化对象的每一项
    const processedObj = {};
    for (const key in input) {
      if (input.hasOwnProperty(key)) {
        const value = input[key];

        // 仅格式化包含小数点的字符串
        if (typeof value === 'string' && value.includes('.')) {
          processedObj[key] = processNumber(value);
        } else {
          processedObj[key] = value; // 保留其他值
        }
      }
    }
    return processedObj;
  } else {
    // 如果既不是数组也不是对象，直接返回原值
    return input;
  }
}
